import React, { useEffect, useRef, memo } from 'react';
import { createChart, CandlestickSeries, HistogramSeries } from 'lightweight-charts';

/**
 * CandlestickChart — TradingView Lightweight Charts candlestick + volume chart.
 * Renders OHLCV data fetched from Birdeye/Binance API via proxy.
 * Supports light/dark theme toggle and configurable timeframes.
 *
 * @param {string} coinId - Coin identifier (bitcoin, ethereum, solana)
 * @param {number} currentPrice - Latest live price for real-time candle update
 * @param {string} theme - 'light' or 'dark'
 * @param {string} interval - Candle interval (1m, 5m, 15m, 1H, 4H, 1D, 1W)
 * @param {number} hoursBack - Hours of history to fetch
 */
const CandlestickChart = memo(({ coinId, currentPrice, theme = 'dark', interval = '1m', hoursBack = 2 }) => {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const candleSeriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);
    const lastCandleRef = useRef(null);

    // Shift UTC timestamps to local time for chart display
    const tzOffsetSec = new Date().getTimezoneOffset() * -60;
    const toLocal = (utcSec) => utcSec + tzOffsetSec;

    // Initialize chart ONCE on mount
    useEffect(() => {
        if (!containerRef.current) return;

        const chart = createChart(containerRef.current, {
            layout: {
                background: { color: 'transparent' },
                textColor: '#ddd',
                fontSize: 11,
            },
            grid: {
                vertLines: { color: 'rgba(255,255,255,0.03)' },
                horzLines: { color: 'rgba(255,255,255,0.03)' },
            },
            crosshair: {
                mode: 0,
                vertLine: { color: 'rgba(255,255,255,0.1)', width: 1, style: 2 },
                horzLine: { color: 'rgba(255,255,255,0.1)', width: 1, style: 2 },
            },
            rightPriceScale: {
                borderColor: 'rgba(255,255,255,0.05)',
                scaleMargins: { top: 0.05, bottom: 0.2 },
                textColor: '#ccc',
            },
            timeScale: {
                borderColor: 'rgba(255,255,255,0.05)',
                timeVisible: true,
                secondsVisible: false,
                tickMarkFormatter: (time) => {
                    const d = new Date(time * 1000);
                    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                },
            },
            handleScroll: { mouseWheel: false, pressedMouseMove: true },
            handleScale: { mouseWheel: false, pinch: false },
        });

        const candleSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#22c55e',
            downColor: '#ef4444',
            borderUpColor: '#22c55e',
            borderDownColor: '#ef4444',
            wickUpColor: '#22c55e',
            wickDownColor: '#ef4444',
            lastValueVisible: true,
            priceLineVisible: true,
            priceLineColor: '#4ade80',
        });

        const volumeSeries = chart.addSeries(HistogramSeries, {
            priceScaleId: 'volume',
            priceFormat: { type: 'volume' },
        });

        chart.priceScale('volume').applyOptions({
            scaleMargins: { top: 0.85, bottom: 0 },
        });

        chartRef.current = chart;
        candleSeriesRef.current = candleSeries;
        volumeSeriesRef.current = volumeSeries;

        // Handle resize
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                chart.applyOptions({ width, height });
            }
        });
        resizeObserver.observe(containerRef.current);

        // Double-click resets chart to default view (time + price axes)
        const container = containerRef.current;
        const handleDblClick = () => {
            chart.timeScale().fitContent();
            chart.priceScale('right').applyOptions({ autoScale: true });
        };
        container.addEventListener('dblclick', handleDblClick);

        return () => {
            container.removeEventListener('dblclick', handleDblClick);
            resizeObserver.disconnect();
            chart.remove();
            chartRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply theme changes dynamically (no remount)
    useEffect(() => {
        if (!chartRef.current) return;
        const isLight = theme === 'light';

        chartRef.current.applyOptions({
            layout: {
                background: { color: isLight ? '#ffffff' : 'transparent' },
                textColor: isLight ? '#333' : '#ddd',
            },
            grid: {
                vertLines: { color: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.03)' },
                horzLines: { color: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.03)' },
            },
            crosshair: {
                vertLine: { color: isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)' },
                horzLine: { color: isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)' },
            },
            rightPriceScale: {
                borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.05)',
                textColor: isLight ? '#444' : '#ccc',
            },
            timeScale: {
                borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.05)',
            },
        });
    }, [theme]);

    // Fetch OHLCV data when coinId, interval, or hoursBack changes
    useEffect(() => {
        const fetchOHLCV = async () => {
            try {
                const response = await fetch(
                    `/api/ohlcv/${coinId}?interval=${interval}&hours=${hoursBack}`
                );
                if (!response.ok) return;
                const data = await response.json();

                if (data.items && data.items.length > 0 && candleSeriesRef.current) {
                    const candles = data.items.map(item => ({
                        time: toLocal(item.time),
                        open: item.open,
                        high: item.high,
                        low: item.low,
                        close: item.close,
                    }));

                    const volumes = data.items.map(item => ({
                        time: toLocal(item.time),
                        value: item.volume,

                        color: item.close >= item.open
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(239,68,68,0.3)',
                    }));

                    candleSeriesRef.current.setData(candles);
                    volumeSeriesRef.current.setData(volumes);
                    lastCandleRef.current = candles[candles.length - 1];

                    chartRef.current?.timeScale().fitContent();
                }
            } catch (err) {
                // Silently fail — chart stays empty until next fetch
            }
        };

        fetchOHLCV();

        // Refresh interval: faster for short timeframes, slower for long
        const refreshMs = hoursBack <= 2 ? 60000 : hoursBack <= 168 ? 300000 : 600000;
        const timer = setInterval(fetchOHLCV, refreshMs);
        return () => clearInterval(timer);
    }, [coinId, interval, hoursBack]);

    // Update last candle in real-time with live price (only for short timeframes)
    useEffect(() => {
        if (!currentPrice || !candleSeriesRef.current || !lastCandleRef.current) return;
        // Only do real-time updates for minute-level candles
        if (interval !== '1m' && interval !== '5m') return;

        const intervalMs = interval === '1m' ? 60000 : 300000;
        const now = toLocal(Math.floor(Date.now() / intervalMs) * (intervalMs / 1000));
        const lastCandle = lastCandleRef.current;

        if (now > lastCandle.time) {
            // New candle
            const newCandle = {
                time: now,
                open: currentPrice,
                high: currentPrice,
                low: currentPrice,
                close: currentPrice,
            };
            candleSeriesRef.current.update(newCandle);
            volumeSeriesRef.current.update({
                time: now,
                value: 0,
                color: 'rgba(34,197,94,0.3)',
            });
            lastCandleRef.current = newCandle;
        } else {
            // Update existing candle
            const updatedCandle = {
                ...lastCandle,
                high: Math.max(lastCandle.high, currentPrice),
                low: Math.min(lastCandle.low, currentPrice),
                close: currentPrice,
            };
            candleSeriesRef.current.update(updatedCandle);
            lastCandleRef.current = updatedCandle;
        }
    }, [currentPrice, interval]);

    return (
        <div
            ref={containerRef}
            style={{ width: '100%', height: '100%' }}
        />
    );
});

CandlestickChart.displayName = 'CandlestickChart';

export default CandlestickChart;
